import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Check stored auth on mount
    useEffect(() => {
        const token = localStorage.getItem('banyan_token');
        const storedUser = localStorage.getItem('banyan_user');

        if (token && storedUser) {
            try {
                setUser(JSON.parse(storedUser));
                // Verify token is still valid
                apiClient.authMe()
                    .then((data) => {
                        setUser(data.user);
                        setPermissions(data.permissions || []);
                        localStorage.setItem('banyan_user', JSON.stringify(data.user));
                    })
                    .catch(() => {
                        // Token expired
                        logout();
                    })
                    .finally(() => setLoading(false));
            } catch {
                logout();
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, []);

    const login = useCallback(async (username, password, captchaToken) => {
        const result = await apiClient.authLogin(username, password, captchaToken);
        localStorage.setItem('banyan_token', result.token);
        localStorage.setItem('banyan_user', JSON.stringify(result.user));
        setUser(result.user);
        setPermissions(result.permissions || []);
        return result;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('banyan_token');
        localStorage.removeItem('banyan_user');
        setUser(null);
        setPermissions([]);
    }, []);

    const hasPermission = useCallback((menuKey) => {
        if (!user) return false;
        if (user.role === 'super_admin') return true;
        const perm = permissions.find(p => p.menu_key === menuKey);
        return perm ? perm.can_access : false;
    }, [user, permissions]);

    const hasEditPermission = useCallback((menuKey) => {
        if (!user) return false;
        if (user.role === 'super_admin') return true;
        const perm = permissions.find(p => p.menu_key === menuKey);
        return perm ? perm.can_edit : false;
    }, [user, permissions]);

    const refreshPermissions = useCallback(async () => {
        try {
            const data = await apiClient.authMe();
            setPermissions(data.permissions || []);
            setUser(data.user);
            localStorage.setItem('banyan_user', JSON.stringify(data.user));
        } catch (err) {
            console.error('Failed to refresh permissions:', err);
        }
    }, []);

    const value = {
        user,
        permissions,
        loading,
        isAuthenticated: !!user,
        isSuperAdmin: user?.role === 'super_admin',
        isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
        login,
        logout,
        hasPermission,
        hasEditPermission,
        refreshPermissions,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
